import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, BellOff, Save, CalendarDays, FileText, PenTool, Shield, Megaphone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { toast } from 'sonner';

const CATEGORIES = [
  {
    key: 'appointments',
    icon: CalendarDays,
    title: 'Appointments',
    description: 'Booking confirmations, reschedules, cancellations and reminders.',
  },
  {
    key: 'documents',
    icon: FileText,
    title: 'Documents',
    description: 'Upload receipts, specialist requests for missing items and tax-file updates.',
  },
  {
    key: 'signatures',
    icon: PenTool,
    title: 'Signature requests',
    description: 'When your tax specialist needs a signed authorization or form.',
  },
  {
    key: 'security',
    icon: Shield,
    title: 'Security',
    description: 'Password resets and sign-in verification codes. Always on for your safety.',
    locked: true,
  },
  {
    key: 'marketing',
    icon: Megaphone,
    title: 'Tips & news',
    description: 'Occasional tax-saving tips, deadline reminders and product news.',
  },
];

function Toggle({ checked, disabled, onChange, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
        disabled ? 'cursor-not-allowed opacity-60' : ''
      } ${checked ? 'bg-[#f5b300]' : 'bg-neutral-300'}`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

export default function MemberNotificationsPage() {
  const nav = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.getEmailPreferences();
        setPrefs(data.prefs || {});
      } catch {
        toast.error('Could not load notification settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const data = await api.updateEmailPreferences({ prefs });
      setPrefs(data.prefs || {});
      setDirty(false);
      toast.success('Notification preferences saved');
    } catch {
      toast.error('Could not save preferences');
    } finally {
      setSaving(false);
    }
  };

  const anyOn = prefs && Object.entries(prefs).some(([k, v]) => v && k !== 'security');

  return (
    <>
      <Header />
      <section className="py-12 bg-[#fbf7ee] border-b border-neutral-100">
        <div className="max-w-[820px] mx-auto px-6">
          <Link to="/account" data-testid="back-to-account"
                className="inline-flex items-center gap-1.5 text-[13px] text-neutral-500 hover:text-[#14110f] mb-4">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to account
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#f5b300]/15 text-[#c89200] flex items-center justify-center">
              {anyOn ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
            </div>
            <div>
              <p className="section-eyebrow mb-1">Email notifications</p>
              <h1 className="text-[28px] md:text-[32px] font-extrabold text-[#14110f] leading-tight">
                Choose what we email you about
              </h1>
              <p className="text-[13.5px] text-neutral-600 mt-1">
                Update anytime — we&rsquo;ll always send security alerts and a few critical tax-file updates.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-[820px] mx-auto px-6">
          {loading ? (
            <div data-testid="notifications-loading" className="py-16 text-center text-neutral-400 text-[14px]">
              Loading your preferences…
            </div>
          ) : (
            <>
              <ul data-testid="notification-categories"
                  className="divide-y divide-neutral-100 bg-white border border-neutral-200 rounded-xl overflow-hidden">
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  const value = !!prefs?.[c.key];
                  return (
                    <li key={c.key} className="flex items-start gap-4 px-5 py-5">
                      <div className="w-10 h-10 rounded-md bg-[#fff8e3] text-[#c89200] flex items-center justify-center shrink-0">
                        <Icon className="w-4.5 h-4.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-[15px] font-bold text-[#14110f]">{c.title}</h3>
                          {c.locked && (
                            <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500">
                              Always on
                            </span>
                          )}
                        </div>
                        <p className="text-[13px] text-neutral-500 mt-0.5">{c.description}</p>
                      </div>
                      <Toggle
                        testId={`notification-toggle-${c.key}`}
                        checked={c.locked ? true : value}
                        disabled={!!c.locked}
                        onChange={(v) => toggle(c.key, v)}
                      />
                    </li>
                  );
                })}
              </ul>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-3 px-1">
                <p className="text-[12.5px] text-neutral-500">
                  Sender: <code className="text-[12px]">notifications@mail.wysetribe.ca</code>
                </p>
                <button
                  data-testid="notifications-save-btn"
                  onClick={save}
                  disabled={!dirty || saving}
                  className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] disabled:bg-neutral-200 disabled:text-neutral-500 disabled:cursor-not-allowed text-[#14110f] font-semibold px-5 py-2.5 rounded-md transition"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
}
